import i18next from 'i18next'
import * as React from 'react'
import { safeParseUrl } from '../../../shared/lib/url.no-deps'
import { Pulse } from '../ui/Pulse'
import type { CommonDeps } from '../base/CommonDeps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { GuidePresentationWithParentInfo } from './GuidePresentationWithParentInfo'
import type { ParentInfo, UrlGuideExecution } from './Model'
import { isMobile } from '../../../shared/lib/isMobile'

interface Props {
    deps: CommonDeps
    guideExecution: UrlGuideExecution
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onDone: () => void
    renderCachedImage: RenderCachedImage
}

const isCordova = Boolean(window?.cordova)

/**
 * A component that is used for either opening an URL in a new browser tab
 * or for displaying an error message if the URL is invalid.
 */
export function UrlGuidePresentation(props: Props) {
    const url = safeParseUrl(props.guideExecution.guide.url)
    const target = isCordova ? '_system' : '_blank'

    if (url === undefined) {
        // Invalid URL
        return <Content {...props}>{i18next.t('appcore:Guide.invalidUrl')}</Content>
    }

    // In Safari, you cannot call window.open() anywhere except directly in the
    // event handler. We are a long, long way from the event handler by the time
    // we get here! So, for iOS we will render a link on screen and just direct
    // the user to click it.

    if (isMobile.iOS()) {
        return (
            <Content {...props}>
                <a
                    href={String(url)}
                    target={target}
                    rel="noopener noreferrer"
                    onClick={props.onDone}
                    className=".AppCore__guide__UrlGuide-Link"
                >
                    {i18next.t('appcore:Guide.clickToOpenGuide')}
                </a>
            </Content>
        )
    }

    // For all other browsers, we can just open the link on mount, and we're done.
    // We will render a loading animation while the tab is opening.
    React.useEffect(() => {
        window.open(url, target)
        props.onDone()
    }, [])

    return (
        <Content {...props}>
            <Pulse />
        </Content>
    )
}

function Content(props: React.PropsWithChildren<Omit<Props, 'onDone'>>) {
    return (
        <GuidePresentationWithParentInfo
            deps={props.deps}
            guideParentInfo={props.guideExecution.parentInfo}
            onClickGuideParentInfo={props.onClickGuideParentInfo}
            renderCachedImage={props.renderCachedImage}
        >
            <div className="AppCore__guide__UrlGuide">{props.children}</div>
        </GuidePresentationWithParentInfo>
    )
}
